import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { X, RefreshCw, Play, Square, ChevronDown, ChevronUp } from 'lucide-react';
import { ethers } from 'ethers';
import { Wallet, Config, MarketCapDCASettings, LogEntry } from '../types';
import { apiService } from '../services/api';
import InlinePriceDisplay from './InlinePriceDisplay';
import BalanceUSD from './BalanceUSD';
import { useTokenPrice } from '../hooks/useTokenPrice';
import { formatBalance, isValidPrivateKey, getTokenSymbol, isNativeETH } from '../utils/helpers';

const WalletContainer = styled.div`
  background: var(--bg-primary);
  border-radius: 10px;
  padding: 16px;
  box-shadow: var(--shadow-md);
  position: relative;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
`;

const WalletHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 8px;
`;

const WalletTitle = styled.div`
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
  flex: 1;
  transition: color 0.3s ease;
`;

const WalletInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  min-width: 0;
`;

const ProgressInfo = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  font-weight: normal;
  white-space: nowrap;
  transition: color 0.3s ease;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }
`;

const InputGroup = styled.div`
  margin-bottom: 10px;
`;

const Label = styled.label`
  display: block;
  color: var(--text-secondary);
  font-size: 0.8125rem;
  font-weight: 500;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: color 0.3s ease;
`;

const Input = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.8125rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    background: var(--bg-tertiary);
    color: var(--text-muted);
  }
`;

const AddressDisplay = styled.div`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 0.875rem;
  font-family: 'Courier New', monospace;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  box-sizing: border-box;
  word-break: break-all;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-hover);
  }
`;

const BalanceContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const BalanceBox = styled.div`
  flex: 1;
  padding: 10px;
  background: var(--bg-secondary);
  border-radius: 6px;
  text-align: center;
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
`;

const BalanceLabel = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  margin-bottom: 3px;
  transition: color 0.3s ease;
  font-weight: 500;
`;

const BalanceValue = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
`;

const RefreshButton = styled.button`
  background: none;
  border: none;
  color: var(--accent-primary);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  margin-left: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--bg-tertiary);
  }
  
  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const OperationTypeContainer = styled.div`
  margin-bottom: 15px;
`;

const OperationButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
`;

const OperationButton = styled.button<{ active: boolean; type: 'buy' | 'sell' }>`
  flex: 1;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  background: ${props => props.active 
    ? (props.type === 'buy' ? 'var(--success)' : 'var(--error)')
    : 'var(--bg-secondary)'
  };
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.active 
    ? (props.type === 'buy' ? 'var(--success)' : 'var(--error)')
    : 'var(--border-color)'
  };
  
  &:hover {
    background: ${props => props.active 
      ? (props.type === 'buy' ? '#16a34a' : '#dc2626')
      : 'var(--bg-tertiary)'
    };
  }
`;

const AmountRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
`;

const AmountGroup = styled.div`
  flex: 1;
`;

const AmountInput = styled(Input)<{ currencyLength: number }>`
  text-align: left;
  padding-left: ${props => Math.max(50, props.currencyLength * 8 + 30)}px;
`;

const CurrencyLabel = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 600;
  pointer-events: none;
  background: var(--bg-primary);
  padding: 0 6px;
  z-index: 1;
  transition: all 0.3s ease;
`;

const AmountInputContainer = styled.div`
  position: relative;
`;

const IntervalInput = styled(Input)`
  text-align: center;
`;

const ActionButton = styled.button<{ variant: 'start' | 'stop' }>`
  width: 100%;
  padding: 12px;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s ease;
  
  background: ${props => props.variant === 'start' ? 'var(--success)' : 'var(--error)'};
  color: white;
  
  &:hover {
    background: ${props => props.variant === 'start' ? '#16a34a' : '#dc2626'};
  }
  
  &:disabled {
    background: var(--text-muted);
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.div<{ status: string }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 8px;
  
  background: ${props => {
    switch (props.status) {
      case 'running': return '#4CAF50';
      case 'stopped': return '#ff9800';
      case 'completed': return '#2196F3';
      case 'error': return '#f44336';
      case 'error_stopped': return '#dc2626';
      case 'waiting_marketcap': return '#9C27B0';
      default: return '#6b7280';
    }
  }};
  color: white;
`;

const ErrorInfo = styled.div`
  background: var(--error);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
  border: 1px solid var(--error);
  border-radius: 6px;
  padding: 8px 12px;
  margin: 8px 0;
  font-size: 0.75rem;
  transition: all 0.3s ease;
`;

const ErrorTitle = styled.div`
  color: var(--error);
  font-weight: 600;
  margin-bottom: 4px;
  transition: color 0.3s ease;
`;

const ErrorMessage = styled.div`
  color: var(--text-primary);
  word-break: break-word;
  transition: color 0.3s ease;
`;

const MarketCapInfo = styled.div`
  background: linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(123, 31, 162, 0.05) 100%);
  border: 1px solid #9C27B0;
  border-radius: 6px;
  padding: 8px 12px;
  margin: 8px 0;
  font-size: 0.75rem;
  transition: all 0.3s ease;
`;

const MarketCapTitle = styled.div`
  color: #9C27B0;
  font-weight: 600;
  margin-bottom: 4px;
  transition: color 0.3s ease;
`;

const MarketCapMessage = styled.div`
  color: var(--text-primary);
  word-break: break-word;
  transition: color 0.3s ease;
`;

const AdvancedSettingsToggle = styled.button`
  background: none;
  border: none;
  color: var(--accent-primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  margin: 8px 0;
  
  &:hover {
    background: var(--bg-secondary);
  }
  
  &:disabled {
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

const AdvancedSettings = styled.div<{ isOpen: boolean }>`
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

interface MarketCapWalletCardProps {
  wallet: Wallet;
  walletNumber: number;
  config: Config | null;
  onUpdate: (id: string, updates: Partial<Wallet>) => void;
  onRemove: (id: string) => void;
  onLog: (level: LogEntry['level'], message: string, strategy?: string) => void;
  onTransactionLog: (strategy_id: string, step: string, details?: any) => void;
}

const MarketCapWalletCard: React.FC<MarketCapWalletCardProps> = ({ 
  wallet, 
  walletNumber,
  config, 
  onUpdate, 
  onRemove,
  onLog,
  onTransactionLog 
}) => {
  // Используем настройки из wallet или дефолтные
  const dcaSettings = wallet.marketCapSettings || {
    action: 'buy',
    checkIntervalSeconds: 10,
    minMarketCap: 100000,
    maxMarketCap: 500000,
    minAmount: 0.005,
    maxAmount: 0.02,
    tradeIntervalSeconds: 300,
    slippage: 0.5,
    maxGasUsd: 2,
  };

  const setDcaSettings = (settings: MarketCapDCASettings) => {
    onUpdate(wallet.id, { marketCapSettings: settings });
  };

  const [isStarting, setIsStarting] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const isLoadingBalanceRef = useRef(false); // Защита от параллельных запросов баланса
  
  // Получаем цены для калькулятора USD
  const { ethPrice, tokenPrice, isLoading: isPriceLoading } = useTokenPrice(config?.tokenAddress);

  // Автоматическое получение адреса из приватного ключа
  useEffect(() => {
    if (wallet.privateKey && isValidPrivateKey(wallet.privateKey) && !wallet.address) {
      try {
        const wallet_ethers = new ethers.Wallet(wallet.privateKey);
        const realAddress = wallet_ethers.address;
        onUpdate(wallet.id, { address: realAddress });
        console.log(`✅ Реальный адрес кошелька получен: ${realAddress}`);
      } catch (error) {
        console.error('❌ Ошибка получения адреса кошелька:', error);
        toast.error('Неверный формат приватного ключа');
      }
    }
  }, [wallet.privateKey, wallet.address, wallet.id, onUpdate]);

  // Автоматическое обновление баланса каждые 30 секунд
  // Объединено в один useEffect чтобы избежать дублирования запросов
  useEffect(() => {
    if (wallet.address && wallet.privateKey && isValidPrivateKey(wallet.privateKey)) {
      // Первая загрузка баланса с небольшой задержкой (чтобы не все кошельки запрашивали одновременно)
      const initialTimeout = setTimeout(() => {
        loadBalance(true);
      }, 100);
      
      // Устанавливаем интервал для автообновления каждые 30 секунд БЕЗ анимации
      const interval = setInterval(() => {
        loadBalance(false); // Без анимации загрузки
      }, 30000); // 30 секунд
      
      // Очищаем таймеры при размонтировании компонента
      return () => {
        clearTimeout(initialTimeout);
        clearInterval(interval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.address, wallet.privateKey]);

  const loadBalance = async (showLoading = true) => {
    if (!wallet.privateKey || !isValidPrivateKey(wallet.privateKey)) return;

    // Защита от параллельных запросов
    if (isLoadingBalanceRef.current) {
      return;
    }
    
    isLoadingBalanceRef.current = true;
    
    if (showLoading) {
      onUpdate(wallet.id, { isLoadingBalance: true });
    }
    
    try {
      if (!config?.rpcUrl || !config?.tokenAddress) {
        throw new Error('Missing config');
      }
      
      // Загружаем баланс целевого токена
      const targetTokenBalance = await apiService.getBalance(
        wallet.privateKey,
        config.rpcUrl,
        config.tokenAddress
      );
      
      // Определяем, является ли quote token нативным ETH
      const quoteIsNativeETH = isNativeETH(config.ethAddress);
      let quoteBalance = 0;
      
      if (quoteIsNativeETH) {
        // Если quote token это нативный ETH, используем eth_balance из ответа
        quoteBalance = targetTokenBalance.eth_balance;
      } else {
        // Если quote token это токен (например USDC), загружаем его баланс отдельно
        try {
          const quoteTokenBalance = await apiService.getBalance(wallet.privateKey, config.rpcUrl, config.ethAddress);
          quoteBalance = quoteTokenBalance.token?.balance || 0;
        } catch (error) {
          // Если не удалось загрузить баланс quote token, используем 0
          quoteBalance = 0;
        }
      }
      
      onUpdate(wallet.id, {
        ethBalance: quoteBalance, // Храним баланс quote token в ethBalance
        tokenBalance: targetTokenBalance.token?.balance || 0,
        isLoadingBalance: false,
      });
      
      const quoteSymbol = getTokenSymbol(config.ethAddress);
      const targetSymbol = getTokenSymbol(config.tokenAddress);
      onLog('info', `Balance loaded for wallet ${walletNumber}: ${quoteBalance.toFixed(4)} ${quoteSymbol}, ${targetTokenBalance.token?.balance?.toFixed(2) || 0} ${targetSymbol}`);
    } catch (error: any) {
      // Логируем только в dev режиме
      if (process.env.NODE_ENV !== 'production') {
        console.error('Failed to load balance:', error);
      }
      onUpdate(wallet.id, { isLoadingBalance: false });
    } finally {
      isLoadingBalanceRef.current = false;
    }
  };

  const handleStartDCA = async () => {
    if (!wallet.privateKey || !isValidPrivateKey(wallet.privateKey)) {
      toast.error('Please enter a valid private key');
      return;
    }
    
    if (wallet.strategyId && wallet.strategyStatus === 'running') {
      toast.error('This wallet already has an active MarketCap DCA strategy');
      return;
    }

    setIsStarting(true);
    
    try {
      const response = await apiService.startMarketCapDCA({
        private_key: wallet.privateKey,
        action: dcaSettings.action,
        check_interval_seconds: dcaSettings.checkIntervalSeconds,
        min_market_cap: dcaSettings.minMarketCap,
        max_market_cap: dcaSettings.maxMarketCap,
        min_amount: dcaSettings.minAmount,
        max_amount: dcaSettings.maxAmount,
        trade_interval_seconds: dcaSettings.tradeIntervalSeconds,
        slippage: dcaSettings.slippage,
        max_gas_usd: dcaSettings.maxGasUsd,
      });

      if (response.task_id) {
        onUpdate(wallet.id, {
          strategyId: response.task_id,
          strategyStatus: 'running',
          strategyAction: dcaSettings.action,
          executed: 0,
          total: 0,
        });
        
        onLog('success', `MarketCap DCA strategy started for wallet ${walletNumber} (${dcaSettings.action.toUpperCase()})`, response.task_id);
        
        onTransactionLog(response.task_id, 'start_execution', {
          execution: 1,
          action: dcaSettings.action,
          minMarketCap: dcaSettings.minMarketCap,
          maxMarketCap: dcaSettings.maxMarketCap,
          amount: dcaSettings.action === 'buy' 
            ? `${dcaSettings.minAmount}-${dcaSettings.maxAmount} ETH` 
            : `${dcaSettings.minAmount}-${dcaSettings.maxAmount} TOKEN`
        });
        
        toast.success(`MarketCap DCA strategy started! ID: ${response.task_id}`);
      }
    } catch (error: any) {
      console.error('Failed to start MarketCap DCA:', error);
      onLog('error', `Failed to start MarketCap DCA strategy for wallet ${walletNumber}: ${error.response?.data?.detail || error.message}`);
      toast.error(error.response?.data?.detail || 'Failed to start MarketCap DCA strategy');
    } finally {
      setIsStarting(false);
    }
  };

  const handleStopDCA = async () => {
    if (!wallet.strategyId) return;
    
    setIsStopping(true);
    
    try {
      const response = await apiService.stopDCA(wallet.strategyId);
      
      if (response.message) {
        onUpdate(wallet.id, {
          strategyStatus: 'stopped',
        });
        
        onLog('info', `MarketCap DCA strategy stopped for wallet ${walletNumber}`, wallet.strategyId);
        toast.success('MarketCap DCA strategy stopped');
      }
    } catch (error: any) {
      console.error('Failed to stop MarketCap DCA:', error);
      onLog('error', `Failed to stop MarketCap DCA strategy for wallet ${walletNumber}: ${error.response?.data?.detail || error.message}`);
      toast.error(error.response?.data?.detail || 'Failed to stop MarketCap DCA strategy');
    } finally {
      setIsStopping(false);
    }
  };

  const getCurrencyLabel = () => {
    if (dcaSettings.action === 'buy') {
      return 'ETH';
    } else {
      return config?.tokenSymbol || 'TOKEN';
    }
  };

  const formatAddress = (address: string) => {
    if (!address) return '';
    if (address.length <= 12) return address;
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  const isRunning = wallet.strategyStatus === 'running' || wallet.strategyStatus === 'waiting_marketcap';

  return (
    <WalletContainer>
      <WalletHeader>
        <WalletTitle>
          📊 Wallet {walletNumber}
          <StatusBadge status={wallet.strategyStatus || 'idle'}>
            {wallet.strategyStatus === 'running' && '🟢 ACTIVE'}
            {wallet.strategyStatus === 'stopped' && '🟡 STOPPED'}
            {wallet.strategyStatus === 'completed' && '✅ COMPLETED'}
            {wallet.strategyStatus === 'error' && '❌ ERROR'}
            {wallet.strategyStatus === 'error_stopped' && '🛑 ERROR STOPPED'}
            {wallet.strategyStatus === 'waiting_marketcap' && '⏳ WAITING'}
            {!wallet.strategyStatus && '🔵 IDLE'}
          </StatusBadge>
        </WalletTitle>
        <WalletInfo>
          {wallet.strategyId && wallet.executed !== undefined && (
            <ProgressInfo>
              Operations: {wallet.executed || 0}
            </ProgressInfo>
          )}
          <CloseButton onClick={() => onRemove(wallet.id)}>
            <X size={16} />
          </CloseButton>
        </WalletInfo>
      </WalletHeader>

      <InputGroup>
        <Label>🔑 Private Key</Label>
        <Input
          type="password"
          placeholder="0x... or without 0x (Private key)"
          value={wallet.privateKey}
          onChange={(e) => onUpdate(wallet.id, { privateKey: e.target.value })}
          disabled={isRunning}
        />
      </InputGroup>

      {wallet.address && (
        <InputGroup>
          <Label>Wallet Address</Label>
          <AddressDisplay
            title={wallet.address}
            onClick={() => {
              navigator.clipboard.writeText(wallet.address);
              toast.success('Address copied to clipboard!');
            }}
          >
            {formatAddress(wallet.address)}
          </AddressDisplay>
        </InputGroup>
      )}

      <InputGroup>
        <Label>
          💰 Balances
          <RefreshButton 
            onClick={() => loadBalance(true)}
            disabled={wallet.isLoadingBalance || !wallet.privateKey}
          >
            <RefreshCw size={14} />
          </RefreshButton>
        </Label>
        <BalanceContainer>
          <BalanceBox>
            <BalanceLabel>{getTokenSymbol(config?.ethAddress)}</BalanceLabel>
            <BalanceValue>
              {wallet.isLoadingBalance ? '...' : formatBalance(wallet.ethBalance)}
            </BalanceValue>
          </BalanceBox>
          <BalanceBox>
            <BalanceLabel>{getTokenSymbol(config?.tokenAddress)}</BalanceLabel>
            <BalanceValue>
              {wallet.isLoadingBalance ? '...' : formatBalance(wallet.tokenBalance)}
            </BalanceValue>
          </BalanceBox>
        </BalanceContainer>
        <BalanceUSD 
          quoteBalance={wallet.ethBalance}
          tokenBalance={wallet.tokenBalance}
          quoteSymbol={getTokenSymbol(config?.ethAddress)}
          tokenSymbol={getTokenSymbol(config?.tokenAddress)}
          quotePrice={isNativeETH(config?.ethAddress) ? ethPrice : (getTokenSymbol(config?.ethAddress) === 'USDC' || getTokenSymbol(config?.ethAddress) === 'USDbC' ? 1 : ethPrice)}
          tokenPrice={tokenPrice}
          isPriceLoading={isPriceLoading}
        />
        <div style={{ fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>
          * Auto-updating every 30 seconds
        </div>
      </InputGroup>

      <OperationTypeContainer>
        <Label>✅ Operation Type</Label>
        <OperationButtons>
          <OperationButton
            active={dcaSettings.action === 'buy'}
            type="buy"
            onClick={() => {
              const newSettings = { ...dcaSettings, action: 'buy' as const };
              setDcaSettings(newSettings);
            }}
            disabled={isRunning}
          >
            🚀 BUY
          </OperationButton>
          <OperationButton
            active={dcaSettings.action === 'sell'}
            type="sell"
            onClick={() => {
              const newSettings = { ...dcaSettings, action: 'sell' as const };
              setDcaSettings(newSettings);
            }}
            disabled={isRunning}
          >
            📈 SELL
          </OperationButton>
        </OperationButtons>
      </OperationTypeContainer>

      <MarketCapInfo>
        <MarketCapTitle>📊 Market Cap DCA Strategy</MarketCapTitle>
        <MarketCapMessage>
          {dcaSettings.action === 'buy' 
            ? `Buy when market cap ≤ $${dcaSettings.minMarketCap.toLocaleString()}, stop when ≥ $${dcaSettings.maxMarketCap.toLocaleString()}`
            : `Sell when market cap ≥ $${dcaSettings.maxMarketCap.toLocaleString()}, stop when ≤ $${dcaSettings.minMarketCap.toLocaleString()}`
          }
        </MarketCapMessage>
      </MarketCapInfo>

      <InputGroup>
        <Label>📉 Min Market Cap (USD)</Label>
        <Input
          type="number"
          step="1000"
          value={dcaSettings.minMarketCap}
          onChange={(e) => {
            const newSettings = { 
              ...dcaSettings, 
              minMarketCap: parseFloat(e.target.value) || 0 
            };
            setDcaSettings(newSettings);
          }}
          disabled={isRunning}
        />
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Minimum market cap threshold
        </div>
      </InputGroup>

      <InputGroup>
        <Label>📈 Max Market Cap (USD)</Label>
        <Input
          type="number"
          step="1000"
          value={dcaSettings.maxMarketCap}
          onChange={(e) => {
            const newSettings = { 
              ...dcaSettings, 
              maxMarketCap: parseFloat(e.target.value) || 0 
            };
            setDcaSettings(newSettings);
          }}
          disabled={isRunning}
        />
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Maximum market cap threshold
        </div>
      </InputGroup>

      <InputGroup>
        <Label>⏱️ Check Interval (seconds)</Label>
        <IntervalInput
          type="number"
          value={dcaSettings.checkIntervalSeconds}
          onChange={(e) => {
            const newSettings = { 
              ...dcaSettings, 
              checkIntervalSeconds: parseInt(e.target.value) || 60 
            };
            setDcaSettings(newSettings);
          }}
          disabled={isRunning}
        />
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          How often to check market cap
        </div>
      </InputGroup>

      <InputGroup>
        <Label>💰 Min Amount</Label>
        <AmountRow>
          <AmountGroup>
            <AmountInputContainer>
              <AmountInput
                type="number"
                step="0.001"
                value={dcaSettings.minAmount}
                currencyLength={getCurrencyLabel().length}
                onChange={(e) => {
                  const newSettings = { 
                    ...dcaSettings, 
                    minAmount: parseFloat(e.target.value) || 0 
                  };
                  setDcaSettings(newSettings);
                }}
                disabled={isRunning}
              />
              <CurrencyLabel>{getCurrencyLabel()}</CurrencyLabel>
            </AmountInputContainer>
          </AmountGroup>
        </AmountRow>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Minimum {getCurrencyLabel()} amount to spend per trade.
        </div>
        <InlinePriceDisplay 
          amount={dcaSettings.minAmount}
          currency={dcaSettings.action === 'buy' ? 'ETH' : 'TOKEN'}
          ethPrice={ethPrice}
          tokenPrice={tokenPrice}
          isPriceLoading={isPriceLoading}
        />
      </InputGroup>

      <InputGroup>
        <Label>💰 Max Amount</Label>
        <AmountRow>
          <AmountGroup>
            <AmountInputContainer>
              <AmountInput
                type="number"
                step="0.001"
                value={dcaSettings.maxAmount}
                currencyLength={getCurrencyLabel().length}
                onChange={(e) => {
                  const newSettings = { 
                    ...dcaSettings, 
                    maxAmount: parseFloat(e.target.value) || 0 
                  };
                  setDcaSettings(newSettings);
                }}
                disabled={isRunning}
              />
              <CurrencyLabel>{getCurrencyLabel()}</CurrencyLabel>
            </AmountInputContainer>
          </AmountGroup>
        </AmountRow>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Maximum {getCurrencyLabel()} amount to spend per trade.
        </div>
        <InlinePriceDisplay 
          amount={dcaSettings.maxAmount}
          currency={dcaSettings.action === 'buy' ? 'ETH' : 'TOKEN'}
          ethPrice={ethPrice}
          tokenPrice={tokenPrice}
          isPriceLoading={isPriceLoading}
        />
      </InputGroup>

      <InputGroup>
        <Label>⏰ Trade Interval (seconds)</Label>
        <IntervalInput
          type="number"
          value={dcaSettings.tradeIntervalSeconds}
          onChange={(e) => {
            const newSettings = { 
              ...dcaSettings, 
              tradeIntervalSeconds: parseInt(e.target.value) || 300 
            };
            setDcaSettings(newSettings);
          }}
          disabled={isRunning}
        />
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
          Time between buy/sell operations
        </div>
      </InputGroup>

      <AdvancedSettingsToggle 
        onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
        disabled={isRunning}
      >
        {showAdvancedSettings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        Advanced Settings
      </AdvancedSettingsToggle>

      <AdvancedSettings isOpen={showAdvancedSettings}>
        <InputGroup>
          <Label>📊 Slippage (%)</Label>
          <IntervalInput
            type="number"
            min="0.1"
            max="50"
            step="0.1"
            value={dcaSettings.slippage}
            onChange={(e) => {
              const newSettings = {
                ...dcaSettings,
                slippage: parseFloat(e.target.value) || 0.5
              };
              setDcaSettings(newSettings);
            }}
            disabled={isRunning}
          />
          <div style={{ fontSize: '0.8rem', color: '#666' }}>
            Maximum slippage tolerance for trades (default: 0.5%).
          </div>
        </InputGroup>

        <InputGroup>
          <Label>⛽ Max Gas Cost (USD)</Label>
          <IntervalInput
            type="number"
            min="0.1"
            max="100"
            step="0.1"
            value={dcaSettings.maxGasUsd}
            onChange={(e) => {
              const newSettings = {
                ...dcaSettings,
                maxGasUsd: parseFloat(e.target.value) || 2
              };
              setDcaSettings(newSettings);
            }}
            disabled={isRunning}
          />
          <div style={{ fontSize: '0.8rem', color: '#666' }}>
            Maximum gas cost per transaction in USD (default: $2).
          </div>
        </InputGroup>
      </AdvancedSettings>

      {/* Информация об ошибках */}
      {wallet.errorCode && wallet.errorMessage && wallet.strategyStatus === 'error_stopped' && (
        <ErrorInfo>
          <ErrorTitle>Critical Error: {wallet.errorCode}</ErrorTitle>
          <ErrorMessage>{wallet.errorMessage}</ErrorMessage>
        </ErrorInfo>
      )}

      <ActionButton
        variant={isRunning ? 'stop' : 'start'}
        onClick={isRunning ? handleStopDCA : handleStartDCA}
        disabled={isStarting || isStopping || !wallet.privateKey}
      >
        {isRunning ? (
          <>
            <Square size={16} />
            {isStopping ? 'STOPPING...' : 'STOP'}
          </>
        ) : (
          <>
            <Play size={16} />
            {isStarting ? 'STARTING...' : `START ${dcaSettings.action.toUpperCase()}`}
          </>
        )}
      </ActionButton>

      {wallet.executed !== undefined && (
        <div style={{ 
          marginTop: '10px', 
          fontSize: '0.9rem', 
          color: '#666', 
          textAlign: 'center' 
        }}>
          Operations completed: {wallet.executed}
        </div>
      )}
    </WalletContainer>
  );
};

export default MarketCapWalletCard;

